import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components';

export const TermsPage: React.FC = () => {
  useEffect(() => {
    document.title = 'Terms of Service | PrintDeck Direct';
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4 sm:px-6 lg:px-8 flex items-center justify-center overflow-x-hidden">
      <div className="w-full max-w-4xl space-y-6">
        {/* Navigation back */}
        <div className="flex items-center justify-between">
          <Link to="/login" className="inline-flex items-center text-xs font-semibold text-blue-600 hover:text-blue-700 transition-colors">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            Back to Sign In
          </Link>
          <div className="flex items-center gap-2">
            <img src="/logo-icon.png" alt="PrintDeck Logo" className="w-6 h-6 object-contain" width={24} height={24} />
            <span className="text-xs font-bold text-slate-900 tracking-tight">PrintDeck Direct</span>
          </div>
        </div>

        {/* Legal Card */}
        <Card className="p-8 sm:p-12 !rounded-[24px] border border-slate-200 bg-white shadow-[0_12px_40px_-12px_rgba(15,23,42,0.08)]">
          <div className="space-y-8">
            {/* Title Header */}
            <div className="border-b border-slate-100 pb-6 text-center sm:text-left">
              <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Terms of Service</h1>
              <p className="mt-2 text-xs text-slate-500">Last updated: June 20, 2026</p>
            </div>

            {/* Document Content */}
            <div className="prose prose-slate max-w-none text-slate-600 text-sm leading-relaxed space-y-6">
              
              <section className="space-y-3">
                <h2 className="text-base font-bold text-slate-900">1. Scope & Acceptance of Agreement</h2>
                <p>
                  Welcome to PrintDeck Direct (hereinafter referred to as the "Service", "we", or "our"). By accessing or utilizing our digital bridge infrastructure, which connects user-authorized WhatsApp communication endpoints to localized physical print processing queues, you agree to comply with and be bound by these Terms of Service.
                </p>
                <p>
                  This Service is provided solely on a business-to-business (B2B) enablement model. If you do not agree to these terms, you must immediately terminate use of the Service.
                </p>
              </section>

              <section className="space-y-3">
                <h2 className="text-base font-bold text-slate-900">2. Service Capabilities & Integration Scope</h2>
                <p>
                  PrintDeck Direct acts as a **semi-automated workflow facilitator**. The Service retrieves document attachments and print parameters sent to your designated integration channels and formats them for one-click printing queues.
                </p>
                <p className="bg-slate-50 border-l-4 border-slate-300 p-3 italic text-slate-500 text-xs">
                  <strong>User Acknowledgment:</strong> PrintDeck Direct is not a manufacturer of printing hardware, nor is it an official division of Meta Platforms, Inc. or WhatsApp Inc. The Service depends entirely on active third-party APIs. Uptime, API compatibility, and file transfer velocities are subject to the policies and code modifications of these third-party platform providers.
                </p>
              </section>

              <section className="space-y-3">
                <h2 className="text-base font-bold text-slate-900">3. Operational Integrity & Mutual Responsibility</h2>
                <p>
                  We employ industry-standard encryption protocols and secure transit channels to protect connection configurations and authentication credentials. Because information routing over public communications networks depends on a shared infrastructure, the Service operates on a mutual responsibility model. We continuously monitor and align our transit systems with modern data protection practices, and we suggest maintaining security-focused configurations (such as secure router gateways and print server settings) on your local hardware environment to support end-to-end operational integrity.
                </p>
              </section>

              <section className="space-y-3">
                <h2 className="text-base font-bold text-slate-900">4. Transient Data Handling</h2>
                <p>
                  To maximize efficiency and respect privacy rights, PrintDeck Direct functions on a **transient processing framework**. Raw customer documents, attachments, and image files are held in temporary memory cache arrays only for the duration required to parse, download, and render the item into your local printing spool. 
                </p>
                <p>
                  We do not archive, analyze, or build databases of the content within files processed through your print queues. The user retains sole administrative responsibility for compliance with local customer privacy expectations.
                </p>
              </section>

              <section className="space-y-3">
                <h2 className="text-base font-bold text-slate-900">5. Limitation of Liability & Hardware Safety</h2>
                <p>
                  Under no circumstances shall PrintDeck Direct, its founders, or affiliates be liable for any indirect, incidental, special, consequential, or punitive damages. This includes, but is not limited to:
                </p>
                <ul className="list-disc pl-5 space-y-1 text-xs">
                  <li>Physical damage or firmware corruption occurring to print hardware connected to the client queue.</li>
                  <li>Loss of business revenue due to delayed print jobs, queue halts, or failed attachment downloads.</li>
                  <li>Data breaches resulting from compromised local operating systems or client-side credentials.</li>
                </ul>
                <p>
                  The Service is provided on an **"as-is" and "as-available"** basis, without express or implied warranties of any kind.
                </p>
              </section>

              <section className="space-y-3">
                <h2 className="text-base font-bold text-slate-900">6. Amendments to Services</h2>
                <p>
                  We reserve the right to alter features, adjust interface behaviors, or update connection limits to maintain operational alignment with security guidelines and third-party API changes. Continued use of PrintDeck Direct following updates indicates agreement to the modified terms.
                </p>
              </section>

            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default TermsPage;
